import { localQueryEntityOptions } from '@app/icare/client/db/client/localQueryOneOptions'
import { sync } from '@app/icare/client/sync'
import type { PatientFormIdType } from '@app/icare/db/schema/form/PatientFormCommon'
import { PatientFormId } from '@app/icare/db/schema/form/PatientFormCommon'
import type { ProgressReportForm } from '@app/icare/db/schema/form/ProgressReportForm'
import { ProgressReportFormSchema } from '@app/icare/db/schema/form/ProgressReportForm'
import type { PatientIdType } from '@app/icare/entities/patient/schema/Patient'
import { PatientId } from '@app/icare/entities/patient/schema/PatientId'
import { appFormOptions } from '@app/icare/form/appFormOptions'
import { useAppForm } from '@app/icare/form/useAppForm'
import { t } from '@lingui/core/macro'
import { zodParams } from '@pkg/db/utils'
import type { EditablePolicy } from '@pkg/form/contexts/editableForm'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { ConfigProvider, Form } from 'antd'
import { useState } from 'react'
import { z } from 'zod'
import { EditProgressReport } from './-edit'

export const Route = createFileRoute('/_app/patienten/$id/_edit/_documents/_forms/verlaufsdokumentation/$form/')({
  component: RouteComponent,
  params: zodParams(z.object({ id: PatientId.ref, form: z.string() })),
  staticData: {
    layout: {
      menuRender: false,
    },
  },
  loader: async ({ params, context: { queryClient } }) => {
    if (params.form === 'new') return

    await queryClient.ensureQueryData(localQueryEntityOptions(params.id))
    await queryClient.ensureQueryData(localQueryEntityOptions(params.form as PatientFormIdType))
  },
})

function RouteComponent() {
  const { id, form: formId } = Route.useParams() as { id: PatientIdType; form: PatientFormIdType | 'new' }
  const router = useRouter()
  const isNew = formId === 'new'
  const [isEditing, setIsEditing] = useState(isNew)

  const current = useQuery(localQueryEntityOptions(formId))
  const editModeAllowed = current.data?.status === 'processing'
  const editablePolicy: Partial<EditablePolicy> = isNew
    ? { forceMode: 'edit' as const }
    : editModeAllowed
      ? { forceMode: isEditing ? 'edit' : ('read' as const) }
      : { forceMode: 'read' as const }
  const isFormInEditMode = editablePolicy?.forceMode === 'edit'

  const form = useAppForm(
    appFormOptions(ProgressReportFormSchema, {
      defaultValues: (current.data as ProgressReportForm) ?? {
        patientId: id,
        type: 'ProgressReport',
        status: 'new',
        id: PatientFormId.generate(),
        form: {
          contact: {
            type: undefined,
            data: undefined,
          },
        },
      },
      toast: { subject: t`Verlaufsdokumentation` },
      onSubmit: async ({ value }) => {
        await sync.mutate(value)
        if (isNew) {
          await router.navigate({ to: '/patienten/$id/verlaufsdokumentation', params: { id } })
        }
      },
    }),
  )

  return (
    <form.AppForm>
      <form.ProForm
        editablePolicy={isNew ? { forceMode: 'edit' } : !editModeAllowed ? { forceMode: 'read' } : undefined}
      >
        <ConfigProvider
          theme={{
            components: {
              Form: {
                itemMarginBottom: isFormInEditMode ? 10 : 0,
              },
            },
          }}
        >
          <Form component={false} layout="horizontal" labelCol={{ flex: '270px' }} wrapperCol={{ flex: '1 1 auto' }}>
            <EditProgressReport
              isNew={isNew}
              isEditModeAllowed={editModeAllowed}
              isEditing={isFormInEditMode}
              onEditModeChange={setIsEditing}
            />
          </Form>
        </ConfigProvider>
      </form.ProForm>
    </form.AppForm>
  )
}
